import { useCallback, useMemo, useState } from 'react';

import {
  type KeyCatalogueEntry,
  type KeyValueKind,
  type KeyValueScalar,
} from '../types/key-value.types';
import { type UseKeyValueCatalogue } from '../useKeyValueCatalogue/useKeyValueCatalogue';

/** What the row component draws. Everything derived, nothing for it to work out. */
export interface KeyValueRowView {
  rowId: string;
  key: string;
  /** The operator's own text for text/number/select; `'true'`/`'false'` for a switch. */
  text: string;
  kind: KeyValueKind;
  entry: KeyCatalogueEntry | null;
  isRequired: boolean;
  isDuplicate: boolean;
  /** The stored value's type contradicts the declared kind; it is shown verbatim. */
  isMismatched: boolean;
  original: KeyValueScalar | undefined;
  isNotANumber: boolean;
  isOutOfRange: boolean;
}

export type KeyValueRowOutcome =
  | { status: 'added'; key: string }
  | { status: 'removed'; key: string }
  | { status: 'refusedMax'; max: number }
  | { status: 'refusedRequired' };

export interface UseKeyValueRowsInput {
  value: Readonly<Record<string, KeyValueScalar>> | null | undefined;
  onValueChange: (value: Record<string, KeyValueScalar>) => void;
  catalogue: UseKeyValueCatalogue;
  /** A ceiling on ADDING. Removing at the ceiling is always allowed. */
  maxRows?: number;
}

export interface UseKeyValueRows {
  rows: readonly KeyValueRowView[];
  canAdd: boolean;
  addRow: (key?: string) => KeyValueRowOutcome;
  removeRow: (rowId: string) => KeyValueRowOutcome;
  setKey: (rowId: string, key: string) => void;
  setText: (rowId: string, text: string) => void;
  setBoolean: (rowId: string, isOn: boolean) => void;
  /** Drops a mismatched value and lets the row start again in its declared kind. */
  adoptKind: (rowId: string) => void;
  /** Installs a whole map at once — what an import lands through. */
  applyMap: (next: Record<string, KeyValueScalar>) => void;
}

/** One row as it is held, before the catalogue is consulted. */
interface RowState {
  rowId: string;
  key: string;
  text: string;
  /**
   * The value this row arrived with, kept until the operator edits it. It is
   * what a type mismatch is emitted from, verbatim.
   */
  original: KeyValueScalar | undefined;
  isPristine: boolean;
}

interface Draft {
  rows: RowState[];
  /** The last map this hook emitted — what an echo is recognised by. */
  emitted: Record<string, KeyValueScalar>;
  /** The `value` prop as it stood at that moment — what "nothing happened" looks like. */
  seen: Record<string, KeyValueScalar>;
}

function textOf(value: KeyValueScalar): string {
  return typeof value === 'boolean' ? String(value) : String(value);
}

function startingTextFor(kind: KeyValueKind): string {
  return kind === 'boolean' ? 'false' : '';
}

function matchesKind(value: KeyValueScalar, kind: KeyValueKind): boolean {
  if (kind === 'number') return typeof value === 'number';
  if (kind === 'boolean') return typeof value === 'boolean';
  return typeof value === 'string';
}

/** Same keys, same values — **order deliberately ignored** (see `resolveRows`). */
function sameEntries(
  left: Readonly<Record<string, KeyValueScalar>>,
  right: Readonly<Record<string, KeyValueScalar>>,
): boolean {
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;

  return leftKeys.every((key) => Object.hasOwn(right, key) && Object.is(left[key], right[key]));
}

function seedRows(
  value: Readonly<Record<string, KeyValueScalar>>,
  order: (keys: readonly string[]) => string[],
  previous: readonly RowState[],
): RowState[] {
  const byKey = new Map(previous.filter((row) => row.key !== '').map((row) => [row.key, row]));

  return order(Object.keys(value)).map((key) => {
    const entry = value[key] as KeyValueScalar;
    const prior = byKey.get(key);

    // A key that survived keeps its row — and therefore its DOM node, its caret
    // and its scroll position. Only its text is refreshed, and only if it moved.
    if (prior !== undefined && prior.isPristine && Object.is(prior.original, entry)) {
      return prior;
    }

    return {
      rowId: prior?.rowId ?? `key:${key}`,
      key,
      text: textOf(entry),
      original: entry,
      isPristine: true,
    };
  });
}

/**
 * The rows for this render — a **pure derivation**, deliberately not a `useMemo`
 * and deliberately not an effect.
 *
 * React Compiler has `set-state-in-render` AND `set-state-in-effect` at error
 * level in this workspace, so React's documented "adjust state when a prop
 * changes" pattern and the effect route are both unavailable. They are also both
 * unnecessary: which rows to show is a function of (`value`, `draft`), and a
 * function is what this is. It is not memoized because a memo that can return
 * one of its own inputs is where `preserve-manual-memoization` bites and the
 * compiler then skips optimizing the whole hook.
 */
function resolveRows(
  value: Readonly<Record<string, KeyValueScalar>>,
  draft: Draft | null,
  order: (keys: readonly string[]) => string[],
): RowState[] {
  if (draft === null) return seedRows(value, order, []);

  // TWO ways for the incoming value to mean "nothing happened out there", and
  // both have to be recognised or the operator's own typing is thrown away.
  //
  //   `emitted` — the owner applied our change and handed it straight back.
  //   `seen`    — the owner has NOT applied it yet (or refused it), so the prop
  //               still holds exactly what it held when we committed.
  //
  // Only a value that matches neither is a genuine outside replacement. Missing
  // the second case is the subtle one: an owner that validates before applying,
  // or simply re-renders late, would otherwise see every keystroke revert.
  //
  // Compared by ENTRIES, never by order: the value round-trips through a jsonb
  // column, which does not preserve object key order, and reading a reordered
  // echo as an outside change would make the editor re-sort itself after a save.
  if (sameEntries(draft.emitted, value) || sameEntries(draft.seen, value)) return draft.rows;

  return seedRows(value, order, draft.rows);
}

/**
 * The rows behind a `key-value` map, and the one place a map is built from them.
 *
 * The contract with the owner is that of any controlled field: every edit emits
 * the whole map, and the owner is expected to hand it back. An owner that
 * refuses the change is telling this hook the value did not change, and the rows
 * reseed accordingly — which is the correct reading of a rejected edit, not a
 * bug in it.
 */
export function useKeyValueRows({
  value,
  onValueChange,
  catalogue,
  maxRows,
}: UseKeyValueRowsInput): UseKeyValueRows {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [nextRowId, setNextRowId] = useState(0);

  const current = useMemo(() => value ?? {}, [value]);
  const { orderKeys, kindFor, entryFor, isRequired } = catalogue;

  const rowStates = resolveRows(current, draft, orderKeys);

  /** The map these rows describe, or `null` when they cannot describe one. */
  const buildMap = useCallback(
    (rows: readonly RowState[]): Record<string, KeyValueScalar> | null => {
      const out: Record<string, KeyValueScalar> = {};
      const seen = new Set<string>();

      for (const row of rows) {
        const key = row.key.trim();
        if (key === '') continue;
        if (seen.has(key)) return null;
        seen.add(key);

        const kind = kindFor(key);

        if (row.isPristine && row.original !== undefined && !matchesKind(row.original, kind)) {
          out[key] = row.original;
          continue;
        }

        if (kind === 'number') {
          const parsed = Number(row.text);
          if (row.text.trim() === '' || Number.isNaN(parsed)) return null;
          out[key] = parsed;
          continue;
        }

        out[key] = kind === 'boolean' ? row.text === 'true' : row.text;
      }

      return out;
    },
    [kindFor],
  );

  /**
   * The one write path. Rows are always kept — an operator's typing is theirs —
   * and the map is emitted only when the rows can describe one, so a duplicate
   * key or a half-typed number leaves the form holding its last valid value.
   */
  const commit = useCallback(
    (rows: RowState[]) => {
      const map = buildMap(rows);

      setDraft((previous) => ({
        rows,
        emitted: map ?? previous?.emitted ?? {},
        seen: current,
      }));
      if (map !== null) onValueChange(map);
    },
    [buildMap, current, onValueChange],
  );

  const patch = useCallback(
    (rowId: string, change: (row: RowState) => RowState) => {
      commit(rowStates.map((row) => (row.rowId === rowId ? change(row) : row)));
    },
    [commit, rowStates],
  );

  const addRow = useCallback(
    (key = ''): KeyValueRowOutcome => {
      if (maxRows !== undefined && rowStates.length >= maxRows) {
        return { status: 'refusedMax', max: maxRows };
      }

      commit([
        ...rowStates,
        {
          rowId: `new:${String(nextRowId)}`,
          key,
          text: startingTextFor(kindFor(key)),
          original: undefined,
          isPristine: false,
        },
      ]);
      setNextRowId(nextRowId + 1);

      return { status: 'added', key };
    },
    [commit, kindFor, maxRows, nextRowId, rowStates],
  );

  const removeRow = useCallback(
    (rowId: string): KeyValueRowOutcome => {
      const row = rowStates.find((candidate) => candidate.rowId === rowId);
      if (row === undefined) return { status: 'refusedRequired' };
      // A required key has no remove affordance rendered; refusing here too is
      // the choke point, not a second one — the component never offers it.
      if (isRequired(row.key)) return { status: 'refusedRequired' };

      commit(rowStates.filter((candidate) => candidate.rowId !== rowId));

      return { status: 'removed', key: row.key };
    },
    [commit, isRequired, rowStates],
  );

  const setKey = useCallback(
    (rowId: string, key: string) => {
      patch(rowId, (row) => ({ ...row, key }));
    },
    [patch],
  );

  const setText = useCallback(
    (rowId: string, text: string) => {
      patch(rowId, (row) => ({ ...row, text, isPristine: false }));
    },
    [patch],
  );

  const setBoolean = useCallback(
    (rowId: string, isOn: boolean) => {
      patch(rowId, (row) => ({ ...row, text: String(isOn), isPristine: false }));
    },
    [patch],
  );

  const adoptKind = useCallback(
    (rowId: string) => {
      patch(rowId, (row) => ({
        ...row,
        text: startingTextFor(kindFor(row.key)),
        original: undefined,
        isPristine: false,
      }));
    },
    [kindFor, patch],
  );

  const applyMap = useCallback(
    (next: Record<string, KeyValueScalar>) => {
      commit(seedRows(next, orderKeys, []));
    },
    [commit, orderKeys],
  );

  const rows = useMemo<KeyValueRowView[]>(() => {
    const counts = new Map<string, number>();
    for (const row of rowStates) {
      const key = row.key.trim();
      if (key === '') continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return rowStates.map((row) => {
      const key = row.key.trim();
      const kind = kindFor(key);
      const entry = entryFor(key);
      const isMismatched =
        row.isPristine && row.original !== undefined && !matchesKind(row.original, kind);
      const parsed = Number(row.text);
      const isNumeric = kind === 'number' && !isMismatched;
      const isNotANumber = isNumeric && (row.text.trim() === '' || Number.isNaN(parsed));

      return {
        rowId: row.rowId,
        key: row.key,
        text: row.text,
        kind,
        entry,
        isRequired: isRequired(key),
        isDuplicate: key !== '' && (counts.get(key) ?? 0) > 1,
        isMismatched,
        original: row.original,
        isNotANumber,
        isOutOfRange:
          isNumeric &&
          !isNotANumber &&
          ((entry?.min !== undefined && parsed < entry.min) ||
            (entry?.max !== undefined && parsed > entry.max)),
      };
    });
  }, [entryFor, isRequired, kindFor, rowStates]);

  return {
    rows,
    canAdd: maxRows === undefined || rowStates.length < maxRows,
    addRow,
    removeRow,
    setKey,
    setText,
    setBoolean,
    adoptKind,
    applyMap,
  };
}
